import type { GalleryConfig } from './gallery-config';

import {
  Alert,
  Box,
  Button,
  Grid,
  Heading,
  Image,
  Text,
} from '@chakra-ui/react';

export interface GalleryBlockProps {
  config: GalleryConfig;
  parseError?: string | null;
  canEdit?: boolean;
  onEdit?: () => void;
}

export function GalleryBlock({
  config,
  parseError,
  canEdit = false,
  onEdit,
}: GalleryBlockProps) {
  const itemCount = config.items.length;
  const columnCount = config.columnCount ?? 3;

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
      >
        <Box>
          <Heading as="h2" size="sm">
            Gallery
          </Heading>
          <Text fontSize="sm" color="fg.muted">
            {itemCount === 0
              ? `Empty gallery, ${columnCount} columns`
              : `${itemCount} image${itemCount === 1 ? '' : 's'}, ${columnCount} columns`}
          </Text>
        </Box>
        {onEdit ? (
          <Button
            size="xs"
            variant="outline"
            onClick={onEdit}
            disabled={!canEdit}
          >
            Edit gallery
          </Button>
        ) : null}
      </Box>

      {parseError ? (
        <Alert.Root status="warning" mt={3} borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Invalid gallery config</Alert.Title>
            <Alert.Description>{parseError}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {itemCount > 0 ? (
        <Grid
          mt={3}
          templateColumns={`repeat(${columnCount}, minmax(0, 1fr))`}
          gap={3}
        >
          {config.items.map((item) => (
            <Box key={`${item.url}-${item.caption ?? ''}`}>
              <Image
                src={item.url}
                alt={item.caption ?? ''}
                borderRadius="md"
                objectFit="cover"
                aspectRatio={1}
                width="100%"
              />
              {item.caption ? (
                <Text mt={1} fontSize="sm" color="fg.muted">
                  {item.caption}
                </Text>
              ) : null}
            </Box>
          ))}
        </Grid>
      ) : (
        <Box
          mt={3}
          px={4}
          py={5}
          borderWidth="1px"
          borderStyle="dashed"
          borderRadius="md"
          color="fg.muted"
        >
          Use Edit gallery to add images.
        </Box>
      )}
    </>
  );
}
